import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList } from 'react-window';
import { getFavorites } from 'flow/actions/favorites-actions';
import { reloadNonAdminPbx, reloadPbx } from 'flow/actions/pbx-actions';
import { firstIndexToFocus, listItemKey, uniqueRowKeys } from 'flow/components/nav/list-keys/list-key-action';
import useListTabStop from 'flow/components/nav/list-keys/useListTabStop';
import useRowSelection from 'flow/components/nav/list-keys/useRowSelection';
import PbxListSkeleton from 'flow/components/loaders/PbxListSkeleton';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { selectIsAdmin } from 'flow/selectors/own-extension-selectors';
import { selectDefaultPBXKey, selectPbxIsLoaded } from 'flow/selectors/queue-selectors';
import { isEmptyNullOrUndefined } from 'flow/utils';
import { browserSupportStorage } from 'flow/utils/browser-storage-utils';
import wrappedLocalStorage from 'flow/utils/local-storage-utils';
import NoMatchOnSearch from './empty-states/NoMatchOnSearch';
import { pbxListRowHeight, pbxListRowKey, rowIsFocusable } from './pbx-list-rows';
import PbxListWindowRow from './PbxListRow';
import usePbxListRows from './usePbxListRows';
import { jsx as _jsx } from "react/jsx-runtime";
var PBX_OPEN = 'pbxOpen';

// A default array would be a new one on every render, and the rows are
// memoized on it.
var ALL_DEPARTMENTS = ['supervisorView', 'favorites', 'ivr', 'queues', 'liveChats', 'voicemails', 'connections', 'aiCoworkers'];
var savePbxState = function savePbxState(department, isOpen) {
  if (browserSupportStorage()) {
    var departmentStates = wrappedLocalStorage.isSet(PBX_OPEN) ? JSON.parse(wrappedLocalStorage.getItem(PBX_OPEN)) : {};
    departmentStates[department] = isOpen;
    wrappedLocalStorage.setItem(PBX_OPEN, JSON.stringify(departmentStates));
  }
};
var PbxList_TW = function PbxList_TW(_ref) {
  var _departmentStates$fav, _departmentStates$ref, _departmentStates$que, _departmentStates$cha, _departmentStates$voi, _departmentStates$con, _departmentStates$aiC;
  var selectedPbx = _ref.selectedPbx,
    onItemSelected = _ref.onItemSelected,
    filterText = _ref.filterText,
    _ref$selectDefaultIte = _ref.selectDefaultItem,
    selectDefaultItem = _ref$selectDefaultIte === void 0 ? true : _ref$selectDefaultIte,
    _ref$enabledDepartmen = _ref.enabledDepartments,
    enabledDepartments = _ref$enabledDepartmen === void 0 ? ALL_DEPARTMENTS : _ref$enabledDepartmen,
    _ref$enableCallButton = _ref.enableCallButton,
    enableCallButton = _ref$enableCallButton === void 0 ? true : _ref$enableCallButton,
    _ref$marksOpenPage = _ref.marksOpenPage,
    marksOpenPage = _ref$marksOpenPage === void 0 ? true : _ref$marksOpenPage;
  var _useAppContext = useAppContext(),
    i18next = _useAppContext.i18next;
  var dispatch = useAppDispatch();
  var isAdmin = useAppSelector(selectIsAdmin);
  var defaultPBXKey = useAppSelector(selectDefaultPBXKey);
  var pbxIsLoaded = useAppSelector(selectPbxIsLoaded);
  var containerRef = useRef(null);
  var listRef = useRef(null);
  var _useState = useState(selectedPbx),
    _useState2 = _slicedToArray(_useState, 2),
    selected = _useState2[0],
    setSelected = _useState2[1];
  var departmentStates = wrappedLocalStorage.isSet(PBX_OPEN) ? JSON.parse(wrappedLocalStorage.getItem(PBX_OPEN)) : {};
  var _useState3 = useState({
      favorites: (_departmentStates$fav = departmentStates.favorites) !== null && _departmentStates$fav !== void 0 ? _departmentStates$fav : true,
      refers: (_departmentStates$ref = departmentStates.refers) !== null && _departmentStates$ref !== void 0 ? _departmentStates$ref : true,
      queues: (_departmentStates$que = departmentStates.queues) !== null && _departmentStates$que !== void 0 ? _departmentStates$que : true,
      channels: (_departmentStates$cha = departmentStates.channels) !== null && _departmentStates$cha !== void 0 ? _departmentStates$cha : true,
      voicemails: (_departmentStates$voi = departmentStates.voicemails) !== null && _departmentStates$voi !== void 0 ? _departmentStates$voi : true,
      connections: (_departmentStates$con = departmentStates.connections) !== null && _departmentStates$con !== void 0 ? _departmentStates$con : true,
      aiCoworkers: (_departmentStates$aiC = departmentStates.aiCoworkers) !== null && _departmentStates$aiC !== void 0 ? _departmentStates$aiC : true
    }),
    _useState4 = _slicedToArray(_useState3, 2),
    departmentExpanded = _useState4[0],
    setDepartmentExpanded = _useState4[1];
  var rows = usePbxListRows({
    filterText: filterText,
    departmentExpanded: departmentExpanded,
    enabledDepartments: enabledDepartments
  });
  // react-window keys each row element by its key, so focus and the row's
  // own state, such as an open menu, stay with its item when rows come in
  // above it. The Tab stop and the selection name their row by it too.
  var rowKeys = useMemo(function () {
    return uniqueRowKeys(rows, pbxListRowKey);
  }, [rows]);
  useEffect(function () {
    dispatch(getFavorites());
    dispatch(isAdmin ? reloadPbx() : reloadNonAdminPbx());
  }, [dispatch, isAdmin]);

  // A favourite queue is in two rows, and only one shows the selection. The
  // list shares the rule with the contact list (useRowSelection).
  var noChoiceIndex = useCallback(function () {
    return rows.findIndex(function (row) {
      return row.kind === 'item' && row.key === selected;
    });
  }, [rows, selected]);
  var _useRowSelection = useRowSelection({
      rowKeys: rowKeys,
      openItemKey: selected,
      noChoiceIndex: noChoiceIndex
    }),
    selectionStore = _useRowSelection.selectionStore,
    findSelectedIndex = _useRowSelection.findSelectedIndex,
    chooseRow = _useRowSelection.chooseRow;
  var selectedOrFirstIndex = useCallback(function () {
    var selectedIndex = findSelectedIndex();
    return selectedIndex === -1 ? firstIndexToFocus(rows, rowIsFocusable) : selectedIndex;
  }, [rows, findSelectedIndex]);
  var _useListTabStop = useListTabStop({
      containerRef: containerRef,
      listRef: listRef,
      rows: rows,
      rowKeys: rowKeys,
      rowIsFocusable: rowIsFocusable,
      entryIndex: selectedOrFirstIndex
    }),
    tabStopStore = _useListTabStop.tabStopStore,
    onFocus = _useListTabStop.onFocus,
    onKeyDown = _useListTabStop.onKeyDown,
    onItemsRendered = _useListTabStop.onItemsRendered,
    onRowUnmountWithFocus = _useListTabStop.onRowUnmountWithFocus;
  useEffect(function () {
    if (selectDefaultItem && selectedPbx === undefined) {
      setSelected(defaultPBXKey);
    }
  }, [selectDefaultItem, selectedPbx, defaultPBXKey]);

  // The page can change without a click in the list, as by Back.
  useLayoutEffect(function () {
    if (selectedPbx) {
      setSelected(selectedPbx);
    }
  }, [selectedPbx]);
  var onSelect = useCallback(function (index, key, item) {
    setSelected(key);
    chooseRow(index, key);
    onItemSelected === null || onItemSelected === void 0 || onItemSelected(key, item);
  }, [onItemSelected, chooseRow]);

  // One function for the list's life, so a section title that does not
  // change does not render again when another section folds.
  var onToggleSection = useCallback(function (department, isOpen) {
    setDepartmentExpanded(function (previousExpanded) {
      return _objectSpread(_objectSpread({}, previousExpanded), {}, _defineProperty({}, department, !isOpen));
    });
    savePbxState(department, !isOpen);
  }, []);

  // A page without a key, such as a conference, has no row: the selection
  // is then the default item, not the open page.
  var selectionIsOpenPage = marksOpenPage && !!selectedPbx;
  var itemData = useMemo(function () {
    return {
      rows: rows,
      selectionStore: selectionStore,
      tabStopStore: tabStopStore,
      rowKeys: rowKeys,
      enableCallButton: enableCallButton,
      selectionIsOpenPage: selectionIsOpenPage,
      onSelect: onSelect,
      onToggleSection: onToggleSection,
      onRowUnmountWithFocus: onRowUnmountWithFocus
    };
  }, [rows, selectionStore, tabStopStore, rowKeys, enableCallButton, selectionIsOpenPage, onSelect, onToggleSection, onRowUnmountWithFocus]);

  // Each list above turns a missing slice into an empty List, so an empty
  // list cannot tell "not fetched yet" from "nothing here".
  //
  // This only catches a cold start, where no part of the pbx has answered.
  // It does not cover the window while the slices arrive one by one: queues
  // is already fetched by the session bootstrap (app-actions getClientDTO)
  // and by AppToolbar, so this is true again long before the list opens,
  // while voicemails are only ever fetched below. Closing that window needs
  // the store to track pbx requests in flight.
  if (!pbxIsLoaded) {
    return /*#__PURE__*/_jsx(PbxListSkeleton, {});
  }

  // The pbx slices answer one at a time, so an empty list on its own does
  // not mean there is nothing to show - the rest may still be on its way.
  // Only a search that matched nothing is worth telling the user about.
  // Same condition as ContactList.
  var searchFoundNothing = !isEmptyNullOrUndefined(filterText) && rows.length === 0;
  if (listRef.current) {
    // Reset the whole list item height cache here. We could calculate from which index to
    // clear the cache here but that would add extra complexity and will be hard to maintain,
    // so we go with this approach and will optimize it if we see that it's necessary.
    listRef.current.resetAfterIndex(0, false);
  }
  return /*#__PURE__*/_jsx("div", {
    // NOSONAR
    // Not a control: see useListTabStop.
    ref: containerRef,
    className: "left-nav-scroll-wrapper h-full flex-auto",
    "data-context-menu": "false",
    onFocus: onFocus,
    onKeyDown: onKeyDown,
    children: searchFoundNothing ? /*#__PURE__*/_jsx(NoMatchOnSearch, {
      reserveScrollbarGutter: true,
      children: i18next.t('PBX.SearchEmpty.Description', {
        filterText: filterText
      })
    }) : /*#__PURE__*/_jsx(AutoSizer, {
      className: "size-full",
      disableWidth: true,
      children: function children(_ref2) {
        var height = _ref2.height;
        return /*#__PURE__*/_jsx(VariableSizeList, {
          ref: listRef,
          overscanCount: 7,
          height: height,
          width: "100%",
          itemData: itemData,
          itemCount: rows.length,
          itemKey: listItemKey,
          itemSize: function itemSize(index) {
            return pbxListRowHeight(rows[index]);
          },
          onItemsRendered: onItemsRendered,
          className: "scrollbar left-nav-scroll w-full",
          style: {
            overflowY: 'scroll',
            direction: undefined
          },
          children: PbxListWindowRow
        });
      }
    })
  });
};
export default PbxList_TW;